import { useState } from "react";
import { toast } from "sonner";
import { marketingApi } from "@/lib/marketing";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Field, TextInput, TextArea, Select, PURPOSES, copyToClipboard } from "./shared";

function parseRecipientLine(line) {
  const parts = line.split(",").map((p) => p.trim());
  const [name, company, role, sequence] = parts;
  const recipient = { name };
  if (company) recipient.company = company;
  if (role) recipient.role = role;
  if (sequence) recipient.sequence = sequence;
  return recipient;
}

export default function BulkTab({ onCreated }) {
  const [names, setNames] = useState("");
  const [baseAlias, setBaseAlias] = useState("");
  const [purpose, setPurpose] = useState("outreach");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [channel, setChannel] = useState("");
  const [placement, setPlacement] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [results, setResults] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const lines = names.split("\n").map((n) => n.trim()).filter(Boolean);
    if (lines.length === 0) {
      setErr("Paste at least one name, one per line.");
      return;
    }
    setBusy(true);
    try {
      const res = await marketingApi.batchCreate({
        campaign_key: baseAlias, purpose, destination_url: destinationUrl,
        channel: channel || null, placement: placement || null,
        recipients: lines.map(parseRecipientLine),
      });
      setResults(res);
      const created = res.filter((r) => r.status === "created").length;
      const dup = res.filter((r) => r.status === "duplicate").length;
      toast.success(`${created} link${created === 1 ? "" : "s"} created${dup ? `, ${dup} already existed` : ""}`);
      onCreated();
    } catch (e2) {
      setErr(e2.message || "Bulk create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.1fr_1.4fr] gap-8" data-testid="bulk-tab">
      <form onSubmit={submit} className="space-y-4 bg-paper border border-ink/12 p-6" data-testid="bulk-create-form">
        <h2 className="font-serif text-xl text-ink mb-2">Bulk generator</h2>
        <Field label="Names" hint="One recipient per line. Never used in the public URL. Optional columns: Name, Company, Role, Sequence (comma-separated).">
          <TextArea rows={6} value={names} onChange={(e) => setNames(e.target.value)} placeholder={"Jane Doe\nJohn Smith, Acme Corp, VP Sales, touch 1 of 3"} data-testid="bulk-names-textarea" />
        </Field>
        <Field label="Base alias" hint="Shared campaign label grouping this batch — the short link itself always stays a random, opaque URL.">
          <TextInput required value={baseAlias} onChange={(e) => setBaseAlias(e.target.value)} data-testid="bulk-base-alias-input" />
        </Field>
        <Field label="Destination URL"><TextInput required value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} data-testid="bulk-destination-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Purpose"><Select value={purpose} onChange={setPurpose} options={PURPOSES} data-testid="bulk-purpose-select" /></Field>
          <Field label="Channel"><TextInput value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="bulk-channel-input" /></Field>
        </div>
        <Field label="Placement"><TextInput value={placement} onChange={(e) => setPlacement(e.target.value)} data-testid="bulk-placement-input" /></Field>
        {err && <p className="font-body text-sm text-terracotta" data-testid="bulk-create-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="bulk-create-submit"
          className="w-full bg-ink text-paper py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors disabled:opacity-50">
          {busy ? "Generating…" : "Generate links"}
        </button>
      </form>

      <div className="bg-paper border border-ink/12 overflow-hidden">
        <div className="p-4 border-b border-ink/10">
          <h2 className="font-serif text-xl text-ink">Results ({results.length})</h2>
        </div>
        <Table data-testid="bulk-results-table">
          <TableHeader>
            <TableRow>
              <TableHead className="font-body uppercase tracking-wide text-xs">Name</TableHead>
              <TableHead className="font-body uppercase tracking-wide text-xs">Short URL</TableHead>
              <TableHead className="font-body uppercase tracking-wide text-xs">Status</TableHead>
              <TableHead className="font-body uppercase tracking-wide text-xs" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.length === 0 && (
              <TableRow><TableCell colSpan={4} className="font-body text-ink-soft text-center py-8">Paste names and generate to see results here.</TableCell></TableRow>
            )}
            {results.map((r) => (
              <TableRow key={r.link.link_id} data-testid={`bulk-result-row-${r.link.link_id}`}>
                <TableCell className="font-body text-ink">{r.name}</TableCell>
                <TableCell className="font-body text-ink-soft text-xs">{r.link.short_url}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.status === "duplicate" ? "text-terracotta border-terracotta/40" : "text-ink border-ink/30"} data-testid={`bulk-status-${r.link.link_id}`}>
                    {r.status === "duplicate" ? "already existed" : "created"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <button type="button" onClick={() => copyToClipboard(r.link.short_url, "Short URL")} data-testid={`bulk-copy-${r.link.link_id}`}
                    className="font-body text-xs uppercase tracking-[0.14em] text-terracotta hover:underline">
                    Copy
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
